import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import WhatsAppReviewModal from '../modals/WhatsAppReviewModal';
import { 
  MessageSquare, 
  Brain, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  RefreshCw, 
  Activity
} from 'lucide-react';

const WhatsAppDashboard = () => {
  const { isDarkMode } = useAdminTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [stagingRecords, setStagingRecords] = useState([]);
  const [lowConfidenceRecords, setLowConfidenceRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Cache management
  const [cache, setCache] = useState({
    stats: { data: null, timestamp: 0 },
    staging: { data: null, timestamp: 0 },
    lowConfidence: { data: null, timestamp: 0 }
  });
  
  const CACHE_DURATION = 30000; // 30 seconds cache
  
  // Helper function to check if cache is valid - memoized to prevent recreation
  const isCacheValid = useCallback((cacheKey) => {
    const now = Date.now();
    return cache[cacheKey]?.timestamp && (now - cache[cacheKey].timestamp) < CACHE_DURATION;
  }, [cache]);
  
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl && ['overview', 'staging', 'low-confidence'].includes(tabFromUrl) 
      ? tabFromUrl 
      : 'overview';
  });
  
  // Sub-tab state for AI Processing tab
  const [aiProcessingSubTab, setAiProcessingSubTab] = useState('to-be-reviewed');
  // eslint-disable-next-line no-unused-vars
  const [totalPages, setTotalPages] = useState(1);
  
  // Enhanced loading states
  const [tabLoading] = useState({});
  const [fetchProgress, setFetchProgress] = useState({ isFetching: false, progress: '', logs: [] });
  const [convertingRecord, setConvertingRecord] = useState(null);
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  const [statsLoading] = useState(false);
  const [testingConnections, setTestingConnections] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkRejectReason] = useState('');

  const loadDashboardData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      const requests = [];
      
      // Check cache for each endpoint with timeout
      if (!isCacheValid('stats') || forceRefresh) {
        requests.push(
          Promise.race([
            api.get('/whatsapp/stats'),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Stats timeout')), 10000))
          ]).then(response => ({
            type: 'stats',
            data: response.data
          }))
        );
      }
      
      if (!isCacheValid('staging') || forceRefresh) {
        requests.push(
          Promise.race([
            api.get('/whatsapp/staging-records?page=1&limit=1000'),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Staging timeout')), 10000))
          ]).then(response => ({
            type: 'staging',
            data: response.data
          }))
        );
      }
      
      if (!isCacheValid('lowConfidence') || forceRefresh) {
        requests.push(
          Promise.race([
            api.get('/whatsapp/low-confidence?threshold=0.7&limit=20'),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Low confidence timeout')), 10000))
          ]).then(response => ({
            type: 'lowConfidence',
            data: response.data
          }))
        );
      }

      // If all data is cached, use cached data
      if (requests.length === 0) {
        setStats(cache.stats?.data?.stats || null);
        setStagingRecords(cache.staging?.data?.records || []);
        setLowConfidenceRecords(cache.lowConfidence?.data?.records || []);
        setTotalPages(cache.staging?.data?.pagination?.pages || 1);
        setLoading(false);
        return;
      }

      // Execute API calls in parallel
      const responses = await Promise.all(requests);
      const now = Date.now();

      // Process responses and update cache
      responses.forEach(response => {
        if (response.data.success) {
          const newCacheEntry = { data: response.data, timestamp: now };
          
          switch (response.type) {
            case 'stats':
              setStats(response.data.stats);
              setCache(prev => ({ ...prev, stats: newCacheEntry }));
              break;
            case 'staging':
              setStagingRecords(response.data.records);
              setCache(prev => ({ ...prev, staging: newCacheEntry }));
              break;
            case 'lowConfidence':
              setLowConfidenceRecords(response.data.records);
              setCache(prev => ({ ...prev, lowConfidence: newCacheEntry }));
              break;
            default:
              console.warn('Unknown response type:', response.type);
              break;
          }
        }
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [cache, isCacheValid]);

  const handleFetchMessages = async () => {
    setFetchProgress({
      isFetching: true,
      progress: 'Fetching WhatsApp messages...',
      logs: ['Starting WhatsApp message fetch...']
    });

    try {
      const response = await api.post('/whatsapp/fetch', {
        hours: 24,
        forceReprocess: false
      });

      if (response.data.success) {
        toast.success('WhatsApp messages fetched successfully');
        loadDashboardData();
        
        setFetchProgress(prev => ({
          ...prev,
          logs: [
            ...prev.logs,
            `✅ Fetched ${response.data.summary?.messagesFetched || 0} messages`,
            `📊 Processed ${response.data.summary?.messagesProcessed || 0} messages`,
            `🤖 AI Success Rate: ${response.data.summary?.aiSuccessRate?.toFixed(1) || 0}%`
          ]
        }));
      } else {
        throw new Error(response.data.message || 'Failed to fetch messages');
      }
    } catch (error) {
      console.error('Error fetching WhatsApp messages:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch WhatsApp messages');
      
      setFetchProgress(prev => ({
        ...prev,
        logs: [
          ...prev.logs,
          `❌ Error: ${error.response?.data?.message || error.message}`
        ]
      }));
    } finally {
      setFetchProgress(prev => ({
        ...prev,
        isFetching: false,
        progress: 'Completed'
      }));
      
      setTimeout(() => {
        setFetchProgress({
          isFetching: false,
          progress: '',
          logs: []
        });
      }, 3000);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnections(true);
    try {
      const response = await api.post('/whatsapp/test-connection');
      if (response.data.success) {
        toast.success('WhatsApp API connection is working');
      } else {
        toast.error('WhatsApp API connection failed');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Failed to test WhatsApp API connection');
    } finally {
      setTestingConnections(false);
    }
  };

  const handleReviewRecord = (record) => {
    setSelectedRecord(record);
    setShowReviewModal(true);
  };

  const handleApproveRecord = async (recordId, modifications = {}) => {
    try {
      setConvertingRecord(recordId);
      const response = await api.post(`/whatsapp/staging/${recordId}/approve`, {
        modifications
      });

      if (response.data.success) {
        toast.success('Record approved and payment request created');
        setShowReviewModal(false);
        loadDashboardData();
      } else {
        throw new Error(response.data.message || 'Failed to approve record');
      }
    } catch (error) {
      console.error('Error approving record:', error);
      toast.error(error.response?.data?.message || 'Failed to approve record');
    } finally {
      setConvertingRecord(null);
    }
  };

  const handleRejectRecord = async (recordId, reason) => {
    try {
      const response = await api.post(`/whatsapp/staging/${recordId}/reject`, {
        reason
      });

      if (response.data.success) {
        toast.success('Record rejected');
        setShowReviewModal(false);
        loadDashboardData();
      } else {
        throw new Error(response.data.message || 'Failed to reject record');
      }
    } catch (error) {
      console.error('Error rejecting record:', error);
      toast.error(error.response?.data?.message || 'Failed to reject record');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedRecords.length === 0) {
      toast.error('Please select records to perform bulk action');
      return;
    }

    // Confirmation for delete action
    if (action === 'delete') {
      const confirmed = window.confirm(
        `Are you sure you want to delete ${selectedRecords.length} record(s)? This action cannot be undone and will permanently remove the records from both raw and staging collections.`
      );
      if (!confirmed) {
        return;
      }
    }

    // Set loading state
    setBulkOperationLoading(true);
    setShowBulkModal(false);

    try {
      let response;
      if (action === 'approve') {
        response = await api.post('/whatsapp/staging/bulk-approve', {
          stagingIds: selectedRecords
        });
      } else if (action === 'reject') {
        response = await api.post('/whatsapp/staging/bulk-reject', {
          stagingIds: selectedRecords,
          reason: bulkRejectReason || 'Bulk rejection'
        });
      } else if (action === 'delete') {
        response = await api.post('/whatsapp/staging/bulk-delete', {
          stagingIds: selectedRecords
        });
      }

      if (response?.data.success) {
        toast.success(`Bulk ${action} completed successfully`);
        setSelectedRecords([]);
        loadDashboardData();
      }
    } catch (error) {
      console.error(`Error performing bulk ${action}:`, error);
      toast.error(`Failed to perform bulk ${action}`);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Update URL when activeTab changes
  useEffect(() => {
    if (activeTab !== 'overview') {
      setSearchParams({ tab: activeTab });
    } else {
      setSearchParams({});
    }
  }, [activeTab, setSearchParams]);

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: Activity,
      description: 'WhatsApp processing overview and statistics'
    },
    {
      id: 'staging',
      label: 'AI Processing',
      icon: Brain,
      description: 'Review AI-parsed message data'
    },
    {
      id: 'low-confidence',
      label: 'Human Review',
      icon: AlertTriangle,
      description: 'Review low confidence records'
    },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-skyone-page-dark' : 'bg-gray-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-3xl font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                WhatsApp Management
              </h1>
              <p className={`mt-2 text-lg transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Process WhatsApp messages and manage customer communications
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleTestConnection}
                disabled={testingConnections}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  testingConnections
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {testingConnections ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Activity className="w-4 h-4 mr-2" />
                    Test Connection
                  </>
                )}
              </button>
              <button
                onClick={handleFetchMessages}
                disabled={fetchProgress.isFetching}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  fetchProgress.isFetching
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {fetchProgress.isFetching ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Fetch Messages
                  </>
                )}
              </button>
              <button
                onClick={() => loadDashboardData(true)}
                disabled={loading}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  loading
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Progress Display */}
        {fetchProgress.isFetching && (
          <div className={`mb-6 p-4 rounded-lg transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark' : 'bg-blue-50'
          }`}>
            <div className="flex items-center mb-2">
              <RefreshCw className="w-4 h-4 mr-2 animate-spin text-blue-600" />
              <span className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>{fetchProgress.progress}</span>
            </div>
            <div className="space-y-1">
              {fetchProgress.logs.map((log, index) => (
                <p key={index} className={`text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>{log}</p>
              ))}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className={`mb-6 transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
        } rounded-lg shadow-sm border ${isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'}`}>
          <div className="px-6 py-4 border-b border-gray-200">
            <div className={`flex space-x-1 p-1 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-100'
            }`}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-white shadow-sm text-blue-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                  <span className={`text-lg font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Loading dashboard data...</span>
                </div>
              </div>
            )}
            
            {!loading && activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Statistics Cards */}
                {statsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex items-center space-x-2">
                      <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                      <span className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Loading statistics...</span>
                    </div>
                  </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className={`p-6 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-white'
                  } border ${isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'}`}>
                    <div className="flex items-center">
                      <MessageSquare className={`w-8 h-8 mr-3 transition-colors duration-300 ${
                        isDarkMode ? 'text-blue-400' : 'text-blue-600'
                      }`} />
                      <div>
                        <p className={`text-2xl font-bold transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>{stats?.rawMessages?.total || 0}</p>
                        <p className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>Total Messages</p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-6 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-white'
                  } border ${isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'}`}>
                    <div className="flex items-center">
                      <CheckCircle className={`w-8 h-8 mr-3 transition-colors duration-300 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <div>
                        <p className={`text-2xl font-bold transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>{stats?.rawMessages?.completed || 0}</p>
                        <p className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>Processed</p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-6 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-white'
                  } border ${isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'}`}>
                    <div className="flex items-center">
                      <Clock className={`w-8 h-8 mr-3 transition-colors duration-300 ${
                        isDarkMode ? 'text-orange-400' : 'text-orange-600'
                      }`} />
                      <div>
                        <p className={`text-2xl font-bold transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>{stats?.rawMessages?.pending || 0}</p>
                        <p className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>Pending</p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-6 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-white'
                  } border ${isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'}`}>
                    <div className="flex items-center">
                      <Brain className={`w-8 h-8 mr-3 transition-colors duration-300 ${
                        isDarkMode ? 'text-purple-400' : 'text-purple-600'
                      }`} />
                      <div>
                        <p className={`text-2xl font-bold transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>{(stats?.qualityMetrics?.avgConfidence * 100 || 0).toFixed(1)}%</p>
                        <p className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>AI Confidence</p>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Recent Activity */}
                <div className={`p-6 rounded-lg transition-colors duration-300 ${
                  isDarkMode ? 'bg-skyone-inset-dark' : 'bg-white'
                } border ${isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'}`}>
                  <h3 className={`text-lg font-medium mb-4 transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Recent Activity</h3>
                  <div className="space-y-3">
                    {stagingRecords.slice(0, 5).map((record) => (
                      <div key={record._id} className={`flex items-center justify-between p-3 rounded-lg transition-colors duration-300 ${
                        isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                      }`}>
                        <div className="flex items-center">
                          <MessageSquare className={`w-4 h-4 mr-3 transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`} />
                          <div>
                            <p className={`text-sm font-medium transition-colors duration-300 ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              {record.extractedData?.customer?.name?.value || 'Unknown Customer'}
                            </p>
                            <p className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {record.extractedData?.serviceRequirements?.serviceType?.value || 'N/A'} - 
                              {record.extractedData?.serviceRequirements?.categoryLevel?.value || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            record.stagingStatus === 'approved' 
                              ? 'bg-green-100 text-green-800'
                              : record.stagingStatus === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {record.stagingStatus}
                          </span>
                          <span className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {(record.qualityMetrics?.overallConfidence * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!loading && activeTab === 'staging' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-lg font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>AI Processing Results</h3>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Review and manage AI-parsed message data
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowBulkModal(true)}
                      disabled={selectedRecords.length === 0 || bulkOperationLoading}
                      className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                        selectedRecords.length === 0 || bulkOperationLoading
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {bulkOperationLoading ? 'Processing...' : `Bulk Actions (${selectedRecords.length})`}
                    </button>
                  </div>
                </div>

                {/* AI Processing Sub-tabs */}
                <div className="border-b border-gray-200 dark:border-skyone-border-dark">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setAiProcessingSubTab('to-be-reviewed')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-300 ${
                        aiProcessingSubTab === 'to-be-reviewed'
                          ? isDarkMode
                            ? 'border-blue-500 text-blue-400'
                            : 'border-blue-500 text-blue-600'
                          : isDarkMode
                            ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      To be Reviewed
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                        isDarkMode ? 'bg-skyone-inset-dark text-zinc-300' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {stagingRecords.filter(record => 
                          (record.qualityMetrics?.overallConfidence || 0) >= 0.75 && record.stagingStatus === 'extracted'
                        ).length}
                      </span>
                    </button>
                    <button
                      onClick={() => setAiProcessingSubTab('approved')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-300 ${
                        aiProcessingSubTab === 'approved'
                          ? isDarkMode
                            ? 'border-green-500 text-green-400'
                            : 'border-green-500 text-green-600'
                          : isDarkMode
                            ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Approved
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                        isDarkMode ? 'bg-skyone-inset-dark text-zinc-300' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {stagingRecords.filter(record => 
                          (record.qualityMetrics?.overallConfidence || 0) >= 0.75 && record.stagingStatus === 'approved'
                        ).length}
                      </span>
                    </button>
                  </nav>
                </div>

                {/* Staging Records Table */}
                <div className={`overflow-x-auto transition-colors duration-300 ${
                  isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
                } rounded-lg border ${isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'}`}>
                  <table className="w-full">
                    <thead className={`transition-colors duration-300 ${
                      isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                    }`}>
                      <tr>
                        <th className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRecords(stagingRecords.map(r => r._id));
                              } else {
                                setSelectedRecords([]);
                              }
                            }}
                            className="rounded"
                          />
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-500'
                        }`}>Customer</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-500'
                        }`}>Service</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-500'
                        }`}>Confidence</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-500'
                        }`}>Status</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-500'
                        }`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y transition-colors duration-300 ${
                      isDarkMode ? 'divide-skyone-border-dark' : 'divide-gray-200'
                    }`}>
                      {tabLoading.staging ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                              <span className={`text-sm transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>Loading staging records...</span>
                            </div>
                          </td>
                        </tr>
                      ) : (() => {
                        // Filter records based on selected sub-tab
                        const filteredRecords = stagingRecords.filter(record => {
                          const hasHighConfidence = (record.qualityMetrics?.overallConfidence || 0) >= 0.75;
                          if (aiProcessingSubTab === 'to-be-reviewed') {
                            return hasHighConfidence && record.stagingStatus === 'extracted';
                          } else if (aiProcessingSubTab === 'approved') {
                            return hasHighConfidence && record.stagingStatus === 'approved';
                          }
                          return hasHighConfidence;
                        });
                        
                        return filteredRecords.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-6 py-8 text-center">
                              <span className={`text-sm transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {aiProcessingSubTab === 'to-be-reviewed' 
                                  ? 'No high-confidence records to be reviewed' 
                                  : 'No approved records found'
                                }
                              </span>
                            </td>
                          </tr>
                        ) : (
                          filteredRecords.map((record) => (
                        <tr key={record._id} className={`transition-colors duration-300 ${
                          isDarkMode ? 'hover:bg-skyone-inset-dark' : 'hover:bg-gray-50'
                        }`}>
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedRecords.includes(record._id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRecords([...selectedRecords, record._id]);
                                } else {
                                  setSelectedRecords(selectedRecords.filter(id => id !== record._id));
                                }
                              }}
                              className="rounded"
                            />
                          </td>
                          <td className={`px-6 py-4 text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            <div>
                              <div className="font-medium">
                                {record.extractedData?.customer?.name?.value || 'N/A'}
                              </div>
                              <div className={`text-xs transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {record.extractedData?.customer?.phone?.value || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className={`px-6 py-4 text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            <div>
                              <div className="font-medium">
                                {record.extractedData?.serviceRequirements?.serviceType?.value || 'N/A'}
                              </div>
                              <div className={`text-xs transition-colors duration-300 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {record.extractedData?.serviceRequirements?.categoryLevel?.value || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className={`px-6 py-4 text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            <div className="flex items-center">
                              <div className={`w-2 h-2 rounded-full mr-2 ${
                                record.qualityMetrics?.overallConfidence > 0.8 
                                  ? 'bg-green-500' 
                                  : record.qualityMetrics?.overallConfidence > 0.6 
                                    ? 'bg-yellow-500' 
                                    : 'bg-red-500'
                              }`} />
                              {(record.qualityMetrics?.overallConfidence * 100).toFixed(1)}%
                            </div>
                          </td>
                          <td className={`px-6 py-4 text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              record.stagingStatus === 'approved' 
                                ? 'bg-green-100 text-green-800'
                                : record.stagingStatus === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {record.stagingStatus}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex space-x-2">
                              {record.stagingStatus !== 'approved' && (
                                <button
                                  onClick={() => handleReviewRecord(record)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Review
                                </button>
                              )}
                              {record.stagingStatus === 'approved' && (
                                <span className="text-green-600 font-medium">
                                  ✓ Approved
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!loading && activeTab === 'low-confidence' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-lg font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>Human Review Required</h3>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Review low confidence AI parsing results
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowBulkModal(true)}
                      disabled={selectedRecords.length === 0 || bulkOperationLoading}
                      className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                        selectedRecords.length === 0 || bulkOperationLoading
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                    >
                      {bulkOperationLoading ? 'Processing...' : `Bulk Delete (${selectedRecords.length})`}
                    </button>
                  </div>
                </div>

                {/* Low Confidence Records */}
                <div className="space-y-4">
                  {tabLoading['low-confidence'] ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="flex items-center space-x-2">
                        <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                        <span className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>Loading low-confidence records...</span>
                      </div>
                    </div>
                  ) : lowConfidenceRecords.length === 0 ? (
                    <div className="text-center py-8">
                      <span className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>No low-confidence records found</span>
                    </div>
                  ) : (
                    lowConfidenceRecords.map((record) => (
                    <div key={record._id} className={`p-4 rounded-lg border transition-colors duration-300 ${
                      isDarkMode ? 'bg-skyone-inset-dark border-skyone-border-dark' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedRecords.includes(record._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRecords([...selectedRecords, record._id]);
                              } else {
                                setSelectedRecords(selectedRecords.filter(id => id !== record._id));
                              }
                            }}
                            className="mr-3 rounded"
                          />
                          <AlertTriangle className={`w-4 h-4 mr-2 transition-colors duration-300 ${
                            isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
                          }`} />
                          <span className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            Low Confidence: {(record.qualityMetrics?.overallConfidence * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleReviewRecord(record)}
                            className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                          >
                            Review
                          </button>
                        </div>
                      </div>
                      
                      <div className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <strong>Customer:</strong> {record.extractedData?.customer?.name?.value || 'N/A'}
                          </div>
                          <div>
                            <strong>Phone:</strong> {record.extractedData?.customer?.phone?.value || 'N/A'}
                          </div>
                          <div>
                            <strong>Service:</strong> {record.extractedData?.serviceRequirements?.serviceType?.value || 'N/A'}
                          </div>
                          <div>
                            <strong>Level:</strong> {record.extractedData?.serviceRequirements?.categoryLevel?.value || 'N/A'}
                          </div>
                        </div>
                        
                        {record.qualityMetrics?.missingRequiredFields?.length > 0 && (
                          <div className="mt-2">
                            <strong>Missing Fields:</strong> {record.qualityMetrics.missingRequiredFields.join(', ')}
                          </div>
                        )}
                        
                        {record.reviewReason?.length > 0 && (
                          <div className="mt-2">
                            <strong>Review Reasons:</strong> {record.reviewReason.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Bulk Actions Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`p-6 rounded-lg shadow-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
            }`}>
              <h3 className={`text-lg font-medium mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Bulk Actions</h3>
              <div className="space-y-4">
                <button
                  onClick={() => {
                    handleBulkAction('approve');
                  }}
                  className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                >
                  Approve Selected ({selectedRecords.length})
                </button>
                <button
                  onClick={() => {
                    handleBulkAction('reject');
                  }}
                  className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                >
                  Reject Selected ({selectedRecords.length})
                </button>
                <button
                  onClick={() => {
                    handleBulkAction('delete');
                  }}
                  className="w-full px-4 py-2 bg-red-800 hover:bg-red-900 text-white rounded"
                >
                  Delete Selected ({selectedRecords.length})
                </button>
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="w-full px-4 py-2 bg-primary-700 hover:bg-primary-600 text-white rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Operation Loading Overlay */}
        {bulkOperationLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`p-8 rounded-lg shadow-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
            }`}>
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                <h3 className={`text-lg font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Processing Bulk Operation</h3>
                <p className={`text-sm text-center transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Please wait while we process your bulk action...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Review Modal */}
        {showReviewModal && selectedRecord && (
          <WhatsAppReviewModal
            record={selectedRecord}
            onClose={() => setShowReviewModal(false)}
            onReject={handleRejectRecord}
            onApprove={handleApproveRecord}
            isProcessing={convertingRecord === selectedRecord?._id}
          />
        )}
      </div>
    </div>
  );
};

export default WhatsAppDashboard;
